import { throttle } from 'es-toolkit/compat';
import { useEffect, useMemo, useState } from 'react';
import { useMap, MarkerComponent } from 'shared/map-exports';
import { useMapEvent } from 'features/mapping/hooks/useMapEvent';
import { MarkerEvent } from 'react-map-gl/dist/esm/types';
import { Capacitor } from '@capacitor/core';
import { AircraftIcons } from './Icons';
import {
  MAX_ICON_SCALE,
  MIN_ICON_SCALE,
  getAircraftShadowStyle,
} from './AircraftsLayer.styles';
import { AircraftIcon } from './AircraftIcon';
import {
  AircraftIconVariant,
  AircraftLayerFeatureCollection,
  AircraftLayerFeature,
} from './types';
import { parseAltitudeToZIndex } from './utils';

type AircraftIconsProps = {
  data: AircraftLayerFeatureCollection;
  onClick: (f: AircraftLayerFeature) => void;
  onHover: (f: AircraftLayerFeature | null) => void;
  visible: boolean;
};

type AircraftMarkerProps = {
  feature: AircraftLayerFeature;
  scale: number;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

const AircraftMarker = (props: AircraftMarkerProps): JSX.Element => {
  const { feature, scale, onClick, onMouseEnter, onMouseLeave } = props;
  const [clicked, setClicked] = useState(false);
  const [hovered, setHovered] = useState(false);
  const isMobileApp = Capacitor.isNativePlatform();

  let variant: AircraftIconVariant = 'default';
  if (hovered || feature.properties.selected) {
    variant =
      feature.properties.altitude === 'ground' ? 'activeground' : 'active';
  } else if (feature.properties.altitude === 'ground') {
    variant = 'ground';
  }

  const shadowStyle = useMemo(
    () =>
      getAircraftShadowStyle({
        rotate: feature.properties.rotate,
        altitude: feature.properties.altitude,
        scale,
      }),
    [feature.properties.rotate, feature.properties.altitude, scale],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleClick = (e: MarkerEvent<any, MouseEvent>): void => {
    // Prevent interaction with other layers
    e.originalEvent.stopPropagation();
    setClicked(true);
    onClick();
  };

  const handleMouseEnter = (): void => {
    setHovered(true);
    onMouseEnter();
  };

  const handleMouseLeave = (): void => {
    setHovered(false);
    onMouseLeave();
    if (clicked) {
      setClicked(false);
    }
  };

  const icon = useMemo(
    () =>
      AircraftIcons.find((i) => i.name === feature.properties.icon) ??
      AircraftIcons.find((i) => i.name === 'tankerType2'),
    [feature.properties.icon],
  );

  const finalScale = useMemo(() => {
    const iconScale = icon?.scale ?? 1.0;
    return scale * iconScale;
  }, [icon?.scale, scale]);

  // Use the aircraft's altitude as the zIndex.
  const zIndex = useMemo(
    () => parseAltitudeToZIndex(feature.properties.altitude),
    [feature.properties.altitude],
  );

  return (
    <MarkerComponent
      longitude={feature.geometry.coordinates[0]}
      latitude={feature.geometry.coordinates[1]}
      rotation={feature.properties.rotate}
      rotationAlignment="map"
      anchor="center"
      style={{ zIndex }}
      onClick={handleClick}
    >
      <div
        style={shadowStyle}
        onMouseEnter={isMobileApp ? undefined : handleMouseEnter}
        onMouseLeave={isMobileApp ? undefined : handleMouseLeave}
      >
        {icon && (
          <AircraftIcon icon={icon} variant={variant} scale={finalScale} />
        )}
      </div>
    </MarkerComponent>
  );
};

export const AircraftIconMarkers = (props: AircraftIconsProps): JSX.Element => {
  const { data, onClick, onHover, visible } = props;
  const { current: mapRef } = useMap();
  const [scale, setScale] = useState<number>(MAX_ICON_SCALE);
  const map = mapRef?.getMap();

  // See https://dmitripavlutin.com/react-throttle-debounce/ for why
  // useMemo is used instead of useCallback.
  const updateScale = useMemo(
    () =>
      throttle(() => {
        const zoom = map?.getZoom() ?? 10;
        if (zoom <= 6) {
          setScale(MIN_ICON_SCALE);
        } else if (zoom >= 10) {
          setScale(MAX_ICON_SCALE);
        } else {
          // Interpolate between min/max for zooms between 6 and 10.
          setScale(
            MIN_ICON_SCALE +
              ((zoom - 6) / 4) * (MAX_ICON_SCALE - MIN_ICON_SCALE),
          );
        }
      }, 100),
    [map, setScale],
  );

  // Set the scale correctly on initial load and when the layer becomes visible.
  // `visible` is in the deps because zooming while hidden doesn't fire the
  // `zoom` handler, so the scale would otherwise stay stale until the next zoom.
  useEffect(() => {
    updateScale();
    return () => {};
  }, [updateScale, visible]);

  useMapEvent('zoom', updateScale, map, visible);

  return (
    <>
      {data.features
        .filter(
          (f) =>
            Number.isFinite(f.geometry.coordinates[0]) &&
            Number.isFinite(f.geometry.coordinates[1]),
        )
        .map((f) => (
          <AircraftMarker
            key={f.properties.hex}
            feature={f}
            scale={scale}
            onClick={() => onClick(f)}
            onMouseEnter={() => onHover(f)}
            onMouseLeave={() => onHover(null)}
          />
        ))}
    </>
  );
};
